/**
Project: Voice Panel (c)
Title: Service Number Row
Description: Component for update/remove phone numbers and routing policies of the resource account list.
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { useEffect, useState, memo } from "react";
import { connect } from "react-redux";
import {
  Text,
  Button,
  TableCell,
  TableRow,
  Flex,
  Dropdown,
  CloseIcon,
  EditIcon,
  UndoIcon,
  Tooltip,
  ExclamationCircleIcon,
  DropdownItem,
  Image,
  Menu,
  Popup,
} from "@fluentui/react-northstar";
import Textfy from "react-texty";
import IntlUtil from "../../../../core/helpers/intl-util";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
import {
  setServiceNumberPhoneNumberToAlter,
  setServiceNumberRoutingPolicyToAlter,
  setServiceNumbersAllRecords,
} from "../actions/service-number-actions";
// import { VoiceAdminURLProps } from "../../common/settings/voice-admin-urls";
import _ from "lodash";
import PropTypes from "prop-types";
import { getCommunicationPlatformByName } from "../../inventory/actions/number-inventory-actions";
import { handleCommunicationPlatformName } from "../../common/actions/general-actions";
import { useDispatch } from "react-redux";

const ServiceNumberRow = ({
  serviceNumber,
  serviceNumbersRoutingPolicies,
  updatePhoneNumber,
  removePhoneNumber,
  editPhoneNumber,
  resetPhoneNumber,
  updateRoutingPolicy,
  removeRoutingPolicy,
  resetRoutingPolicy,
  serviceNumberPhoneNumberToAlter,
  setServiceNumberPhoneNumberToAlter,
  serviceNumberRoutingPolicyToAlter,
  setServiceNumberRoutingPolicyToAlter,
  isAlterDataPropagated,
  intlNamespace,
  phoneNumbersList,
  history,
  editedNumber,
  serviceNumbersAllRecords,
  communicationPlatformsData,
  totalNumbers,
  setServiceRecords,
}) => {
  const [phoneNumber, changePhone] = useState(serviceNumber.phone || "");
  const [policyId, changePolicy] = useState(serviceNumber.policyId || "");
  const [showPhoneControls, togglePhoneControls] = useState(false);
  const [showPolicyControls, togglePolicyControls] = useState(false);
  const [editingPhoneNumber, toggleEditPhoneNumber] = useState(false);
  const [editingRoutingPolicy, toggleEditRoutingPolicy] = useState(false);
  const [resetPolicy, resetPolicyStatus] = useState(false);
  const [resetPhone, resetPhoneStatus] = useState(false);
  const [showResetPhoneControls, toggleResetPhoneControls] = useState(false);
  const [showResetPolicyControls, toggleResetPolicyControls] = useState(false);
  const [phoneNumberTab, setPhoneNumberTab] = useState(0);
  const dispatch = useDispatch();

  useEffect(() => {
    if (serviceNumber.id !== serviceNumberPhoneNumberToAlter) {
      changePhone(serviceNumber.phone);
    }
  }, [serviceNumberPhoneNumberToAlter, serviceNumber.phone, serviceNumber.id]);

  useEffect(() => {
    if (serviceNumber.id !== serviceNumberRoutingPolicyToAlter) {
      changePolicy(serviceNumber.policyId);
    }
  }, [
    serviceNumberRoutingPolicyToAlter,
    serviceNumber.policyId,
    serviceNumber.id,
  ]);

  const assignPhoneNumberStatus = () => {
    setServiceNumberPhoneNumberToAlter(serviceNumber.id);
  };
  // const handleNumberOrder = () => {
  //   history.push(VoiceAdminURLProps.numberOrder);
  //   return;
  // };

  const handleAssignPhoneNumber = async (value) => {
    if (value && value.length > 0) {
      updatePhoneNumber(serviceNumber.id, serviceNumber.email, value, 1);
      resetPhoneStatus(false);
      setServiceNumberPhoneNumberToAlter("");
    }
  };
  const handlePhoneNumberDropdownItemChange = (e, option) => {
    setPhoneNumberTab(option.index);
  };
  const handleResetPhoneNumber = () => {
    if (serviceNumber.originalPhone !== "") {
      updatePhoneNumber(
        serviceNumber.id,
        serviceNumber.email,
        serviceNumber.phone,
        0
      );
      updatePhoneNumber(
        serviceNumber.id,
        serviceNumber.email,
        serviceNumber.originalPhone,
        1
      );
      setServiceNumberPhoneNumberToAlter("");
      resetPhoneNumber(serviceNumber.id, serviceNumber.originalPhone);
      changePhone(serviceNumber.originalPhone);
    } else {
      removePhoneNumber(
        serviceNumber.id,
        serviceNumber.email,
        serviceNumber.phone,
        0
      );
      setServiceNumberPhoneNumberToAlter("");
      resetPhoneNumber(serviceNumber.id, "");
      changePhone(serviceNumber.originalPhone);
    }
  };

  const handleRemovePhoneNumber = async () => {
    resetPhoneStatus(false);
    removePhoneNumber(serviceNumber.id, serviceNumber.email, phoneNumber, 0);
    setServiceNumberPhoneNumberToAlter("");
  };

  const handleEditPhoneNumber = async () => {
    let numList = phoneNumbersList;
    numList = numList.map((res) => {
      if (res.allocateStatus === 0) {
        return res.phoneNumber;
      } else {
        return null;
      }
    });
    numList = numList.filter((res) => {
      return res !== null;
    });
    if (numList.includes(phoneNumber) === false) {
      editPhoneNumber(phoneNumber, 0);
    } else {
      await handlePhoneNumberFocusLoss();
      await togglePhoneControls(false);
    }
  };

  const isResetAvailable = (id, originalPhone) => {
    let resetStatus = true;
    if (originalPhone !== null && originalPhone !== "") {
      serviceNumbersAllRecords.forEach((user) => {
        if (user.id !== id && user.phone === originalPhone) {
          resetStatus = false;
        }
      });
    }
    return resetStatus;
  };

  const handleCancelPhoneNumber = () => {
    handleAssignPhoneNumber(phoneNumber);
    handleResetPhoneNumber();
  };
  const handlePhoneNumberFocusLoss = () => {
    setServiceNumberPhoneNumberToAlter("");
    if (editingPhoneNumber) {
      changePhone(serviceNumber.phone);
      togglePhoneControls(false);
    }
    if (!editingPhoneNumber && phoneNumber.length > 0) {
      changePhone("");
    }
  };

  const changePhoneNumber = async (e, option) => {
    await handleClosePhoneNumberPopup();
    await handleEditPhoneNumber();

    if (option.value === serviceNumber.originalPhone) {
      togglePhoneControls(false);
      handleCancelPhoneNumber();
    } else {
      handleAssignPhoneNumber(option.value);
      //changePhone(option.value);
      togglePhoneControls(false);
    }
  };
  const renderCommunicationPlatform = (number) => {
    let num = null;
    if (number && totalNumbers?.length > 0) {
      num = totalNumbers.find((rec) => rec.phoneNumber === number);
    }

    if (num) {
      let platform = getCommunicationPlatformByName(
        communicationPlatformsData,
        num.communicationPlatform
      );
      if (platform) {
        return (
          <Tooltip content={handleCommunicationPlatformName(platform)}>
            <Image
              className="vpa-platform-image"
              src={platform?.platformIcon}
            />
          </Tooltip>
        );
      } else {
        return <div className="no-number-data"></div>;
      }
    } else {
      return <div className="no-number-data"></div>;
    }
  };
  const renderPhoneNumberData = (Item, props) => {
    let reserveNumbers = [];
    reserveNumbers = phoneNumbersList.map((res) => {
      if (
        res?.reserveStatus?.toLowerCase() ===
        VoiceAdminConstants.USERS.RESERVED_STATUS.RESERVED.toLowerCase()
      ) {
        if (phoneNumberTab === 0) {
          if (
            res.numberCountryName?.toLowerCase().trim() ===
            serviceNumber.country?.toLowerCase()?.trim()
          ) {
            return res.phoneNumber;
          } else {
            return null;
          }
        } else {
          return res.phoneNumber;
        }
      } else {
        return null;
      }
    });
    if (
      props.key === phoneNumber ||
      props.key === serviceNumber.originalPhone
    ) {
      return (
        <>
          <Item
            active={props.active}
            accessibilityItemProps={{
              onMouseEnter: props.accessibilityItemProps.onMouseEnter,
              onMouseMove: props.accessibilityItemProps.onMouseMove,
              onMouseLeave: props.accessibilityItemProps.onMouseLeave,
              id: props.accessibilityItemProps.id,
              onClick: props.accessibilityItemProps.onClick,
            }}
            className={"ui-dropdown__item"}
            header={
              <div className="vpa-platform-number-wrapper ">
                {renderCommunicationPlatform(props.key)}
                <div className="vpa-assigned-phone-dropdown-text-wrapper">
                  <Text
                    {...props}
                    content={props.header}
                    className="text-ff-semibold"
                  />
                  <Tooltip
                    trigger={<ExclamationCircleIcon className="float-right" />}
                    content={IntlUtil.getText(
                      intlNamespace,
                      "content.assignedNumber"
                    )}
                  />
                </div>
              </div>
            }
          />
        </>
      );
    } else if (reserveNumbers.includes(props.key)) {
      return (
        <DropdownItem
          active={props.active}
          accessibilityItemProps={{
            onMouseEnter: props.accessibilityItemProps.onMouseEnter,
            onMouseMove: props.accessibilityItemProps.onMouseMove,
            onMouseLeave: props.accessibilityItemProps.onMouseLeave,
            id: props.accessibilityItemProps.id,
          }}
          header={
            <div className="vpa-platform-number-wrapper">
              {renderCommunicationPlatform(props.key)}
              <div className="vpa-phone-dropdown-text-wrapper">
                <Text
                  {...props}
                  content={props.header}
                  className="page-text text-fc-red"
                />
                <Tooltip
                  trigger={<ExclamationCircleIcon className="float-right" />}
                  content={IntlUtil.getText(
                    intlNamespace,
                    "content.reserveNumberTooltip"
                  )}
                />
              </div>
            </div>
          }
        />
      );
    } else {
      return (
        <>
          {" "}
          <Item
            {...props}
            header={
              <div className="vpa-platform-number-wrapper">
                {renderCommunicationPlatform(props.key)}
                {props.header}
              </div>
            }
          />
        </>
      );
    }
  };
  const renderPolicyDropdown = (sortedPolicies) => {
    return (
      <>
        <Menu
          underlined
          defaultActiveIndex={0}
          primary
          items={[{ key: "Policy", content: "Policy", value: "Policy" }]}
        />
        <div className="vpa-dropdown-wrapper">
          {" "}
          <Dropdown
            fluid
            placeholder={IntlUtil.getText(
              intlNamespace,
              "content.selectPolicy"
            )}
            open={true}
            items={sortedPolicies}
            onChange={changeRoutingPolicy.bind(this)}
            // position="below"
            className="vpa-usn-cell-dropdown m-t-10"
            renderItem={(Item, props) => {
              if (
                props.key === policyId ||
                props.key === serviceNumber.originalPolicyId
              ) {
                return (
                  <Item
                    {...props}
                    header={props.header}
                    className="vpa-users-dropdown-header"
                  />
                );
              } else {
                return <Item {...props} header={props.header} />;
              }
            }}
          />
        </div>
      </>
    );
  };
  const setPhoneNumbersOrder = () => {
    let numbers = [];
    let phoneNumbers = phoneNumbersList;
      if (serviceNumber?.phone && serviceNumber.phone !== "") {
          numbers.push(serviceNumber.phone);
        }
    if (phoneNumberTab === 0) {
      if (phoneNumbers?.length > 0) {
      
        phoneNumbers.forEach((rec) => {
          if (rec.allocateStatus === 0) {
            if (
              rec.numberCountryName?.toLowerCase()?.trim() ===
              serviceNumber.country?.toLowerCase()?.trim()
            ) {
              numbers.push(rec?.phoneNumber);
            }
          }
        });
      }
    } else {
      if (phoneNumbers?.length > 0) {
      
        phoneNumbers.forEach((rec) => {
          if (rec.allocateStatus === 0) {
            numbers.push(rec?.phoneNumber);
          }
        });
      }
    }
    return _.uniq(numbers);
  };
  const handleOpenPhoneNumberPopup = () => {
    setPhoneNumberTab(0);
    let userRecords = [];
    if (serviceNumbersAllRecords?.length > 0) {
      serviceNumbersAllRecords.forEach((usr) => {
        if (usr.id === serviceNumber.id) {
          usr.isPhonePopupOpen = true;
        } else {
          usr.isPhonePopupOpen = false;
        }
        userRecords.push({ ...usr });
      });
      dispatch(setServiceNumbersAllRecords(userRecords));
      setServiceRecords();
    }
  };
  const handleClosePhoneNumberPopup = async () => {
    let userRecords = [];
    if (serviceNumbersAllRecords?.length > 0) {
      serviceNumbersAllRecords.forEach((usr) => {
        if (usr.id === serviceNumber.id) {
          usr.isPhonePopupOpen = false;
        }
        userRecords.push({ ...usr });
      });
      dispatch(setServiceNumbersAllRecords(userRecords));
      setServiceRecords();
      handlePhoneNumberFocusLoss();
    }
  };
  const renderPhoneNumberDropdown = () => {
    return (
      <>
        <Menu
          underlined
          defaultActiveIndex={phoneNumberTab}
          onItemClick={(e, option) =>
            handlePhoneNumberDropdownItemChange(e, option)
          }
          primary
          items={[
            { key: serviceNumber.country, content: serviceNumber.country },
            { key: "All", content: "All" },
          ]}
        />
        <div className="vpa-dropdown-wrapper ">
          {" "}
          <Dropdown
            fluid
            search
            placeholder={IntlUtil.getText(
              intlNamespace,
              "content.selectPhoneNumber"
            )}
            open={true}
            items={setPhoneNumbersOrder()}
            noResultsMessage={IntlUtil.getText(
              intlNamespace,
              "notification.info.noNumbersFound"
            )}
            onChange={changePhoneNumber.bind(this)}
            // position="below"
            className="vpa-usn-cell-dropdown m-t-10"
            
            renderItem={(Item, props) => renderPhoneNumberData(Item, props)}
          />
         
        </div>
      </>
    );
  };
  const renderPhoneNumber = () => {
    let phoneNumbers = phoneNumbersList;
    let reserveNumbers = [];
    if (serviceNumber.id === serviceNumberPhoneNumberToAlter) {
      if (phoneNumbersList && phoneNumbersList.length > 0) {
        phoneNumbers = phoneNumbersList.map((res) => {
          if (res.allocateStatus === 0) {
            return res.phoneNumber;
          } else {
            return "";
          }
        });

        reserveNumbers = phoneNumbersList.map((res) => {
          if (
            res?.reserveStatus?.toLowerCase() ===
            VoiceAdminConstants.USERS.RESERVED_STATUS.RESERVED.toLowerCase()
          ) {
            return res.phoneNumber;
          } else {
            return null;
          }
        });

        phoneNumbers = phoneNumbers.filter((res) => {
          return res !== "";
        });
        phoneNumbers = Array.from(new Set(phoneNumbers));
      } else {
        phoneNumbers = [];
      }
    }
    if (serviceNumber.phone && serviceNumber.phone.length > 0) {
      return (
        <Popup
          open={serviceNumber?.isPhonePopupOpen}
          onOpenChange={async (e, { open }) => {
            if (!open) {
              await handleClosePhoneNumberPopup(); // close popup on outside click
              handlePhoneNumberFocusLoss();
            }
          }}
          pointing
          className="vpa-dropdown-popup"
          content={renderPhoneNumberDropdown()}
        >
          <Flex gap="gap.small">
            {(serviceNumber.phone !== serviceNumber.originalPhone) === false ||
            (resetPhone && phoneNumber !== "") ? (
              <Text content={phoneNumber} className="page-text" />
            ) : (
              <>
                <Text
                  className="page-text"
                  content={`${serviceNumber.phone} ${IntlUtil.getText(
                    intlNamespace,
                    "content.pending"
                  )}`}
                />
                {showResetPhoneControls &&
                  isAlterDataPropagated &&
                  isResetAvailable(
                    serviceNumber.id,
                    serviceNumber.originalPhone
                  ) && (
                    <UndoIcon
                      className="cursor-pointer"
                      onClick={() => {
                        resetPhoneStatus(true);
                        handleResetPhoneNumber();
                      }}
                    />
                  )}
              </>
            )}
            {showPhoneControls && isAlterDataPropagated && (
              <>
                <EditIcon
                  className="cursor-pointer"
                  onClick={() => {
                    toggleEditPhoneNumber(true);
                    handleEditPhoneNumber();
                    handleOpenPhoneNumberPopup();
                    setServiceNumberPhoneNumberToAlter(serviceNumber.id);
                  }}
                />
                <CloseIcon
                  className="cursor-pointer"
                  onClick={() => handleRemovePhoneNumber()}
                />
              </>
            )}
          </Flex>
        </Popup>
      );
    }
    if (
      !serviceNumber.phone ||
      (serviceNumber.phone && serviceNumber.phone.length === 0)
    ) {
      phoneNumbers = phoneNumbersList.map((res) => {
        if (res.allocateStatus === 0) {
          return res.phoneNumber;
        } else {
          return "";
        }
      });

      phoneNumbers = phoneNumbers.filter((res) => {
        return res !== "";
      });
      phoneNumbers = Array.from(new Set(phoneNumbers));
      let filteredNumbers = serviceNumbersAllRecords.map((res) => {
        return res.phone;
      });
      filteredNumbers = filteredNumbers.filter((res) => {
        return res !== "";
      });
      phoneNumbers = phoneNumbers.filter(
        (res) => !filteredNumbers.includes(res)
      );
      if (phoneNumbers && phoneNumbers.length === 1) {
        if (phoneNumbers.includes(editedNumber) === true) {
          phoneNumbers = [];
        }
      }
      return (
        <Flex gap="gap.smaller">
          {
            phoneNumbers && phoneNumbers.length > 0 ? (
              (serviceNumber.phone !== serviceNumber.originalPhone) === false ||
              (phoneNumber === "" && resetPhone) ? (
                <Popup
                  onOpenChange={async (e, { open }) => {
                    if (!open) {
                      await handleClosePhoneNumberPopup(); // close popup on outside click
                    }
                  }}
                  content={renderPhoneNumberDropdown()}
                  open={serviceNumber?.isPhonePopupOpen}
                  pointing
                  trigger={
                    <Button
                      content={IntlUtil.getText(
                        intlNamespace,
                        "content.assignNumber"
                      )}
                      onClick={() => handleOpenPhoneNumberPopup()}
                      text
                      primary
                      className="vpa-usn-cell-button-link"
                      disabled={!isAlterDataPropagated}
                    />
                  }
                />
              ) : (
                <>
                  <Text
                    className="page-text vpa-removed-text"
                    content={IntlUtil.getText(
                      intlNamespace,
                      "content.phoneRemovalPending"
                    )}
                  />
                  {showResetPhoneControls &&
                    isAlterDataPropagated &&
                    isResetAvailable(
                      serviceNumber.id,
                      serviceNumber.originalPhone
                    ) && (
                      <UndoIcon
                        className="cursor-pointer"
                        onClick={() => {
                          handleResetPhoneNumber();
                          resetPhoneStatus(true);
                        }}
                      />
                    )}
                </>
              )
            ) : null
            // (
            //   <Button
            //     content={IntlUtil.getText(intlNamespace, "content.buyNumber")}
            //     onClick={handleNumberOrder}
            //     text
            //     primary
            //     className="vpa-usn-cell-button-link"
            //     disabled={!isAlterDataPropagated}
            //   />
            // )
          }
        </Flex>
      );
    }
  };

  const assignRoutingPolicyStatus = () => {
    setServiceNumberRoutingPolicyToAlter(serviceNumber.id);
  };

  const handleAssignRoutingPolicy = async (value) => {
    if (value && value.length > 0) {
      let selectedValue = "";
      if (
        serviceNumbersRoutingPolicies &&
        serviceNumbersRoutingPolicies.length > 0
      ) {
        selectedValue = await serviceNumbersRoutingPolicies.find((item) => {
          return item === value;
        });
      }
      if (selectedValue && selectedValue !== "") {
        if (value !== serviceNumber.originalPolicyId) {
          updateRoutingPolicy(
            serviceNumber.id,
            serviceNumber.email,
            selectedValue
          );
          setServiceNumberRoutingPolicyToAlter("");
        } else {
          await handleRoutingPolicyFocusLoss();
          await togglePolicyControls(false);
        }
      }
    }
    resetPolicyStatus(false);
  };
  const handleResetPolicy = () => {
    if (serviceNumber.originalPolicyId !== "") {
      updateRoutingPolicy(
        serviceNumber.id,
        serviceNumber.email,
        serviceNumber.originalPolicyId
      );
      setServiceNumberRoutingPolicyToAlter("");
      resetRoutingPolicy(serviceNumber.id, serviceNumber.originalPolicyId);
      changePolicy(serviceNumber.originalPolicyId);
    } else {
      removeRoutingPolicy(serviceNumber.id, serviceNumber.email);
      setServiceNumberRoutingPolicyToAlter("");
      resetRoutingPolicy(serviceNumber.id, "");
      changePolicy(serviceNumber.originalPolicyId);
    }
  };
  const handleRemoveRoutingPolicy = async () => {
    resetPolicyStatus(false);
    removeRoutingPolicy(serviceNumber.id, serviceNumber.email);
    setServiceNumberRoutingPolicyToAlter("");
  };

  const handleRoutingPolicyFocusLoss = () => {
    setServiceNumberRoutingPolicyToAlter("");
    if (editingRoutingPolicy) {
      changePolicy(serviceNumber.policyId);
      toggleEditRoutingPolicy(false);
    }
    if (!editingRoutingPolicy && policyId && policyId.length > 0) {
      changePolicy("");
    }
  };

  const changeRoutingPolicy = async(e, option) => {
    await handleClosePolicyPopup();
    await handleAssignRoutingPolicy(option.value);
    changePolicy(option.value);
    togglePolicyControls(false);

  };
  const handleOpenPolicyPopup = () => {
    let userRecords = [];
    if (serviceNumbersAllRecords?.length > 0) {
      serviceNumbersAllRecords.forEach((usr) => {
        if (usr.id === serviceNumber.id) {
          usr.isPolicyPopupOpen = true;
        } else {
          usr.isPolicyPopupOpen = false;
        }
        userRecords.push({ ...usr });
      });
      dispatch(setServiceNumbersAllRecords(userRecords));
      setServiceRecords();
    }
  };
  const handleClosePolicyPopup = async () => {
    let userRecords = [];
    if (serviceNumbersAllRecords?.length > 0) {
      serviceNumbersAllRecords.forEach((usr) => {
        if (usr.id === serviceNumber.id) {
          usr.isPolicyPopupOpen = false;
        }
        userRecords.push({ ...usr });
      });
      dispatch(setServiceNumbersAllRecords(userRecords));
      await setServiceRecords();
    }
  };
  const renderRoutingPolicy = () => {
    let disorderedPolicies = serviceNumbersRoutingPolicies;
    let sortedPolicies = [];
    if (serviceNumber?.policyId && serviceNumber?.policyId !== "") {
      disorderedPolicies = disorderedPolicies.filter(
        (rec) => rec.toLowerCase() !== serviceNumber?.policyId?.toLowerCase()
      );
      sortedPolicies = _.orderBy(
        disorderedPolicies,
        (policy) => policy.toLowerCase(),
        "asc"
      );
      sortedPolicies.unshift(serviceNumber?.policyId);
    } else {
      sortedPolicies = _.orderBy(
        serviceNumbersRoutingPolicies,
        (policy) => policy.toLowerCase(),
        "asc"
      );
    }

    if (serviceNumber.policyId && serviceNumber.policyId.length > 0) {
      return (
        <Popup
          className="vpa-dropdown-popup"
          pointing
          content={renderPolicyDropdown(sortedPolicies)}
          open={serviceNumber.isPolicyPopupOpen}
          onOpenChange={async (e, { open }) => {
            if (!open) {
              await handleClosePolicyPopup(); // close popup on outside click
              handleRoutingPolicyFocusLoss();
            }
          }}
        >
          <Flex gap="gap.small">
            {(serviceNumber.policyId !== serviceNumber.originalPolicyId) ===
              false ||
            (policyId !== "" && resetPolicy) ? (
              <Text content={policyId} className="page-text" />
            ) : (
              <>
                <Text
                  className="page-text vpa-removed-text"
                  content={`${serviceNumber.policyId} ${IntlUtil.getText(
                    intlNamespace,
                    "content.pending"
                  )}`}
                />
                {showResetPolicyControls && isAlterDataPropagated && (
                  <UndoIcon
                    className="cursor-pointer"
                    onClick={() => {
                      resetPolicyStatus(true);
                      handleResetPolicy();
                    }}
                  />
                )}
              </>
            )}
            {showPolicyControls && isAlterDataPropagated && (
              <>
                <EditIcon
                  className="cursor-pointer"
                  onClick={() => {
                    toggleEditRoutingPolicy(true);
                    handleOpenPolicyPopup();
                    setServiceNumberRoutingPolicyToAlter(serviceNumber.id);
                  }}
                />
                <CloseIcon
                  className="cursor-pointer"
                  onClick={() => handleRemoveRoutingPolicy()}
                />
              </>
            )}
          </Flex>
        </Popup>
      );
    }
    if (
      !serviceNumber.policyId ||
      (serviceNumber.policyId && serviceNumber.policyId.length === 0)
    ) {
      return (
        <Flex gap="gap.smaller">
          {(serviceNumber.policyId !== serviceNumber.originalPolicyId) ===
            false ||
          (policyId === "" && resetPolicy) ? (
            <Popup
              className="vpa-dropdown-popup"
              open={serviceNumber.isPolicyPopupOpen}
              pointing
              onOpenChange={async (e, { open }) => {
                if (!open) {
                  await handleClosePolicyPopup(); // close popup on outside click
                }
              }}
              trigger={
                <Button
                  content={IntlUtil.getText(
                    intlNamespace,
                    "content.assignPolicy"
                  )}
                  onClick={() => handleOpenPolicyPopup()}
                  text
                  primary
                  className="vpa-usn-cell-button-link"
                  disabled={!isAlterDataPropagated}
                />
              }
              content={renderPolicyDropdown(sortedPolicies)}
            />
          ) : (
            <>
              <Text
                className="page-text vpa-removed-text"
                content={IntlUtil.getText(
                  intlNamespace,
                  "content.policyRemovalPending"
                )}
              />
              {showResetPolicyControls && isAlterDataPropagated && (
                <UndoIcon
                  className="cursor-pointer"
                  onClick={() => {
                    resetPolicyStatus(true);
                    handleResetPolicy();
                  }}
                />
              )}
            </>
          )}
        </Flex>
      );
    }
  };
  return (
    <TableRow className="vpa-usn-table-data-row">
      <TableCell className="vpa-usn-table-data-cell">
        <Textfy className="page-text">{serviceNumber.name}</Textfy>
      </TableCell>
      <TableCell className="vpa-usn-table-data-cell">
        <Textfy className="page-text">{serviceNumber.email}</Textfy>
      </TableCell>
       <TableCell className="vpa-usn-table-data-cell max-w-255 min-w-160">
        <Textfy className="page-text">{serviceNumber.country}</Textfy>
      </TableCell>
      <TableCell
        className="vpa-usn-table-data-cell-field"
        onMouseEnter={() =>
          serviceNumber.phone === serviceNumber.originalPhone
            ? togglePhoneControls(true)
            : togglePhoneControls(false) || toggleResetPhoneControls(true)
        }
        onMouseLeave={() =>
          togglePhoneControls(false) || toggleResetPhoneControls(false)
        }
      >
        {renderPhoneNumber()}
      </TableCell>
      <TableCell
        className="vpa-usn-table-data-cell-field"
        onMouseEnter={() =>
          serviceNumber.policyId === serviceNumber.originalPolicyId
            ? togglePolicyControls(true)
            : togglePolicyControls(false) || toggleResetPolicyControls(true)
        }
        onMouseLeave={() =>
          togglePolicyControls(false) || toggleResetPolicyControls(false)
        }
      >
        {renderRoutingPolicy()}
      </TableCell>
      <TableCell className="vpa-usn-table-data-cell">
        <Textfy className="page-text">{serviceNumber.serviceType}</Textfy>
      </TableCell>
    </TableRow>
  );
};
const mapStateToProps = (state) => ({
  serviceNumbersRoutingPolicies:
    state.serviceNumberStore.serviceNumbersRoutingPolicies,
  serviceNumberPhoneNumberToAlter:
    state.serviceNumberStore.serviceNumberPhoneNumberToAlter,
  serviceNumberRoutingPolicyToAlter:
    state.serviceNumberStore.serviceNumberRoutingPolicyToAlter,
  serviceNumbersAllRecords: state.serviceNumberStore.serviceNumbersAllRecords,
  communicationPlatformsData: state.generalStore.communicationPlatformsData,
});
const mapActionToProps = {
  setServiceNumberPhoneNumberToAlter,
  setServiceNumberRoutingPolicyToAlter,
};

ServiceNumberRow.prototype = {
  serviceNumber: PropTypes.object,
  phoneNumbersList: PropTypes.array,
  serviceNumbersRoutingPolicies: PropTypes.array,
};

ServiceNumberRow.defaultProps = {
  phoneNumbersList: [],
  serviceNumbersRoutingPolicies: [],
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(memo(ServiceNumberRow));
